/**
 * MilestoneWidget — celebrates lifetime milestones the store has crossed
 * and shows how far to the next one. Renders inside the All-Time page.
 *
 * Milestones are stored locally (unlock_dates_v1) so the FIRST time you
 * see a fresh unlock we play a subtle sparkle + toast, and thereafter
 * it's just a rendered badge. No backend needed.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Trophy, Star, PartyPopper, Coins, Package, Receipt } from "lucide-react";
import { toast } from "sonner";

const fmtR = (v) => `R ${(Number(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtInt = (v) => (Number(v) || 0).toLocaleString("en-ZA");

// ---------- Milestone tracks ----------
// Each track: current metric value, human label, thresholds, formatter,
// icon, and colour class for the badge chip.
const TRACKS = [
  {
    key: "turnover",
    label: "Lifetime Turnover",
    icon: Coins,
    color: "purple",
    thresholds: [25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_500_000, 5_000_000, 10_000_000, 25_000_000],
    fmt: fmtR,
  },
  {
    key: "gross_profit",
    label: "Lifetime Profit",
    icon: Star,
    color: "emerald",
    thresholds: [5_000, 10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_500_000, 5_000_000],
    fmt: fmtR,
  },
  {
    key: "items_sold",
    label: "Items Sold",
    icon: Package,
    color: "blue",
    thresholds: [100, 500, 1_000, 5_000, 10_000, 25_000, 50_000, 100_000, 250_000],
    fmt: fmtInt,
  },
  {
    key: "transaction_count",
    label: "Transactions Rung",
    icon: Receipt,
    color: "amber",
    thresholds: [100, 500, 1_000, 2_500, 5_000, 10_000, 25_000, 50_000, 100_000],
    fmt: fmtInt,
  },
];

const CHIP_COLORS = {
  purple: "bg-purple-100 text-purple-800 border-purple-300",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
  blue: "bg-blue-100 text-blue-800 border-blue-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
};
const BAR_COLORS = {
  purple: "bg-purple-500",
  emerald: "bg-emerald-500",
  blue: "bg-blue-500",
  amber: "bg-amber-500",
};

const STORAGE_KEY = "milestones_unlocked_v1";

export default function MilestoneWidget({ lifetime }) {
  const [freshUnlocks, setFreshUnlocks] = useState([]);
  const notifiedRef = useRef(false);

  // Load previously-seen unlocks once
  const previouslySeen = useMemo(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
    } catch {
      return new Set();
    }
  }, []);

  const rows = useMemo(() => {
    return TRACKS.map((t) => {
      const value = Number(lifetime?.[t.key] || 0);
      const achieved = t.thresholds.filter((th) => value >= th);
      const next = t.thresholds.find((th) => value < th);
      const lastAchieved = achieved[achieved.length - 1] || null;
      const progressBase = achieved.length > 0 ? achieved[achieved.length - 1] : 0;
      const progressPct = next
        ? Math.min(100, Math.round(((value - progressBase) / (next - progressBase)) * 100))
        : 100;
      return { ...t, value, achieved, lastAchieved, next, progressPct };
    });
  }, [lifetime]);

  // Detect fresh unlocks (first time seeing this achievement)
  useEffect(() => {
    if (notifiedRef.current || !lifetime) return;
    const fresh = [];
    const seen = new Set(previouslySeen);
    for (const r of rows) {
      for (const th of r.achieved) {
        const id = `${r.key}:${th}`;
        if (!previouslySeen.has(id)) {
          fresh.push({ track: r, threshold: th });
          seen.add(id);
        }
      }
    }
    if (fresh.length) {
      // Only celebrate the most impressive fresh unlock — pick the one with
      // the largest ratio of threshold to next threshold so we surface the
      // most meaningful milestone (not just the tiniest R25k tile).
      const highest = fresh.sort((a, b) => b.threshold - a.threshold)[0];
      toast.success(
        `🎉 New milestone unlocked: ${highest.track.label} crossed ${highest.track.fmt(highest.threshold)}!`,
        { duration: 6000 }
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
      setFreshUnlocks(fresh.map((f) => `${f.track.key}:${f.threshold}`));
    }
    notifiedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const totalAchieved = rows.reduce((sum, r) => sum + r.achieved.length, 0);
  const totalPossible = rows.reduce((sum, r) => sum + r.thresholds.length, 0);

  return (
    <Card className="border-2 border-purple-200 shadow-md overflow-hidden" data-testid="milestone-widget">
      <CardHeader className="bg-gradient-to-r from-purple-100 via-fuchsia-50 to-purple-100 border-b border-purple-200 pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-purple-900">
          <PartyPopper className="h-5 w-5" /> Milestone Board
          <span className="ml-auto text-sm font-normal text-purple-700">
            {totalAchieved}/{totalPossible} unlocked
          </span>
        </CardTitle>
        <CardDescription>Achievements your store has crossed since day one</CardDescription>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.key} className="space-y-1.5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`p-1.5 rounded-lg border ${CHIP_COLORS[r.color]}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="text-sm font-semibold text-gray-800 truncate">{r.label}</div>
                  <div className="text-xs text-gray-500">— {r.fmt(r.value)}</div>
                </div>
                <div className="text-xs text-gray-600 whitespace-nowrap">
                  {r.next ? (
                    <>
                      Next: <span className="font-bold text-gray-900">{r.fmt(r.next)}</span>{" "}
                      <span className="text-gray-500">({r.progressPct}%)</span>
                    </>
                  ) : (
                    <span className="text-emerald-700 font-bold flex items-center gap-1">
                      <Trophy className="h-3.5 w-3.5" /> All unlocked!
                    </span>
                  )}
                </div>
              </div>
              {/* Progress bar to next milestone */}
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full ${BAR_COLORS[r.color]} transition-all duration-700`}
                  style={{ width: `${r.progressPct}%` }}
                />
              </div>
              {/* Achievement chips */}
              <div className="flex flex-wrap gap-1.5 pt-1.5">
                {r.thresholds.map((th) => {
                  const done = r.value >= th;
                  const isFresh = freshUnlocks.includes(`${r.key}:${th}`);
                  return (
                    <span
                      key={th}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-all ${
                        done
                          ? `${CHIP_COLORS[r.color]} shadow-sm ${isFresh ? "ring-2 ring-yellow-300 animate-pulse" : ""}`
                          : "bg-gray-50 text-gray-400 border-gray-200"
                      }`}
                      title={done ? "Unlocked" : "Locked"}
                    >
                      {done && <Trophy className="h-3 w-3" />}
                      {r.fmt(th)}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
